import React, { useState, useEffect } from "react";
import { Box, Typography, Grid } from "@mui/material";
import {
  ResponsiveContainer,
  Tooltip,
  BarChart,
  XAxis,
  YAxis,
  Bar,
  CartesianGrid,
  Legend,
} from "recharts";
import { getStatsCompanyAction, cleanStatsAction } from "../redux/actions/stats";
import { useDispatch, useSelector } from "react-redux";
import strings from "../i18n/language";
import InfoIcon from "@mui/icons-material/Info";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";

const BRAND = '#3b95e8';
const GLASS = 'rgba(255,255,255,0.04)';
const BORDER = 'rgba(255,255,255,0.08)';
const MUTED = 'rgba(232,240,254,0.4)';

const contrastColors = [
  '#3b95e8', '#5bbfb5', '#6ab0f0', '#2d9e9a',
  '#8ec6f5', '#4db8b2', '#1a5fa8', '#7dd1cb',
];

function getWeekLabel(weekNumber) {
  const year = new Date().getFullYear();
  const jan4 = new Date(year, 0, 4);
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7) + (weekNumber - 1) * 7);
  const dd = String(monday.getDate()).padStart(2, "0");
  const mm = String(monday.getMonth() + 1).padStart(2, "0");
  return `S${weekNumber} ${dd}/${mm}`;
}

function getContrastColorForNumber(num) {
  return contrastColors[num % contrastColors.length];
}

const card = {
  background: GLASS,
  border: `1px solid ${BORDER}`,
  borderRadius: 20,
  backdropFilter: 'blur(12px)',
  padding: '24px 28px',
  height: '100%',
  boxSizing: 'border-box',
};

const chartDefaults = {
  grid: { stroke: 'rgba(255,255,255,0.06)', strokeDasharray: '4 4' },
  axis: { fill: MUTED, fontSize: 11 },
};



const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(13,27,54,0.95)',
      border: `1px solid ${BORDER}`,
      borderRadius: 10,
      padding: '10px 14px',
      fontSize: 12,
      color: '#e8f0fe',
      backdropFilter: 'blur(8px)',
    }}>
      <div style={{ marginBottom: 6, color: MUTED, fontWeight: 600 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          <div style={{ width: 8, height: 8, borderRadius: 2, background: p.fill }} />
          <span style={{ color: 'rgba(232,240,254,0.7)' }}>{p.name || p.dataKey}:</span>
          <span style={{ fontWeight: 700 }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, info, accent }) => (
  <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 8, position: 'relative', overflow: 'hidden' }}>
    <div style={{
      position: 'absolute', top: 0, left: 0, right: 0, height: 2,
      background: `linear-gradient(90deg, ${accent || BRAND}, transparent)`,
      borderRadius: '20px 20px 0 0',
    }} />
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Typography style={{ fontSize: 12, fontWeight: 600, color: MUTED, letterSpacing: 0.5, textTransform: 'uppercase' }}>
        {label}
      </Typography>
      {Icon && (
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: `rgba(59,149,232,0.12)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon style={{ fontSize: 16, color: accent || BRAND }} />
        </div>
      )}
    </div>
    <Typography style={{ fontSize: 42, fontWeight: 700, color: '#e8f0fe', lineHeight: 1 }}>
      {value}
    </Typography>
    {info && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 4 }}>
        <InfoIcon style={{ fontSize: 13, color: MUTED }} />
        <Typography style={{ fontSize: 12, color: MUTED }}>{info}</Typography>
      </div>
    )}
  </div>
);

const ChartCard = ({ title, children }) => (
  <div style={card}>
    <Typography style={{ fontSize: 14, fontWeight: 600, color: '#e8f0fe', marginBottom: 20, letterSpacing: 0.3 }}>
      {title}
    </Typography>
    {children}
  </div>
);

const DashboardAdmin = () => {
  const dispatch = useDispatch();
  const stats = useSelector((state) => state.stats.stats);
  const [listCompanies, setListCompanies] = useState([]);
  const [dataPointByWeek, setDataPointByWeek] = useState([]);
  const [dataUsersTermsByWeek, setDataUsersTermsByWeek] = useState([]);

  useEffect(() => {
    dispatch(cleanStatsAction());
    dispatch(getStatsCompanyAction());
  }, [dispatch]);

  useEffect(() => {
    if (!stats?.pointsGivenByWeek?.listCompanies) return;

    const listData = [];
    const listUsersTerms = [];
    const activeCompanyIds = new Set();

    Object.entries(stats.pointsGivenByWeek.data || {}).map(([week, data]) => {
      const item = { name: getWeekLabel(week) };
      Object.entries(data).map(([idCompany, points]) => {
        const company = stats.pointsGivenByWeek.listCompanies[idCompany];
        if (company) {
          item[company.name] = points;
          if (points) activeCompanyIds.add(idCompany);
        }
      });
      listData.push(item);
    });

    const listResult = [];
    Object.entries(stats.pointsGivenByWeek.listCompanies).map(([key, value]) => {
      if (activeCompanyIds.has(key)) {
        listResult.push({
          dataKey: value.name,
          fill: getContrastColorForNumber(key),
        });
      }
    });
    if (stats.usersTermsByWeek) {
      Object.entries(stats.usersTermsByWeek).map(([, data]) => {
        listUsersTerms.push(data);
      });
    }

    setListCompanies(listResult);
    setDataPointByWeek(listData);
    setDataUsersTermsByWeek(listUsersTerms);
  }, [stats]);

  if (!stats) return null;

  return (
    <Box sx={{ flexGrow: 1, mx: 2 }}>
      <Grid container spacing={3}>

        {/* Stat cards */}
        <Grid item xs={12} md={6}>
          <StatCard
            icon={BusinessIcon}
            label={strings.LABEL_DASHBOARD_TOTAL_CLIENTS}
            value={stats.numberOfCompanies}
            info={strings.LABEL_DASHBOARD_TOTAL_CLIENTS_INFO}
            accent="#3b95e8"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <StatCard
            icon={PeopleIcon}
            label={strings.LABEL_DASHBOARD_NUM_USUARIOS}
            value={stats.numberOfUsers}
            info={strings.LABEL_DASHBOARD_NUM_USUARIOS_INFO}
            accent="#5bbfb5"
          />
        </Grid>

        {/* Points by week */}
        <Grid item xs={12}>
          <ChartCard title={strings.LABEL_DASHBOARD_POINTS_GIVEN_W}>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={dataPointByWeek} margin={{ top: 4, right: 16, left: -16, bottom: 0 }}>
                <CartesianGrid {...chartDefaults.grid} vertical={false} />
                <XAxis dataKey="name" tick={{ ...chartDefaults.axis }} axisLine={false} tickLine={false} />
                <YAxis tick={{ ...chartDefaults.axis }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: 11, color: MUTED }}
                />
                {listCompanies.map((config, index) => (
                  <Bar
                    key={config.dataKey}
                    dataKey={config.dataKey}
                    fill={config.fill}
                    stackId="points"
                    radius={index === listCompanies.length - 1 ? [4, 4, 0, 0] : 0}
                    barSize={24}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* New users by week */}
        <Grid item xs={12}>
          <ChartCard title={strings.LABEL_DASHBOARD_POINTS_USERS_W}>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={dataUsersTermsByWeek} margin={{ top: 4, right: 16, left: -16, bottom: 0 }}>
                <CartesianGrid {...chartDefaults.grid} vertical={false} />
                <XAxis dataKey="name" tick={{ ...chartDefaults.axis }} axisLine={false} tickLine={false} />
                <YAxis tick={{ ...chartDefaults.axis }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                <Bar dataKey="count" fill={BRAND} fillOpacity={0.85} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

      </Grid>
    </Box>
  );
};

export default DashboardAdmin;
